import { proxy } from 'valtio';

import { createErrorClass } from '@canvas/error';

export const indexeddbStatus = proxy({
  healthy: false,
  healthCheckComplete: false,
});

export const updateIndexeddbStatus = (healthy: boolean) => {
  indexeddbStatus.healthy = healthy;
  indexeddbStatus.healthCheckComplete = true;
  healthCheckPromise = null;
};

export const IndexedDBTimeoutError = createErrorClass('IndexedDBTimeoutError');

const isTest = process.env.MODE === 'test' || process.env.NODE_ENV === 'test';

let timeoutEnabledInTests = false;

/**
 * Enable timeout behavior in test environments.
 * By default, timeouts are disabled in test environments to avoid flaky tests.
 * Call this function in tests that specifically need to test timeout behavior.
 */
export function enableTimeoutInTests() {
  timeoutEnabledInTests = true;
}

/**
 * Disable timeout behavior in test environments (default behavior).
 */
export function disableTimeoutInTests() {
  timeoutEnabledInTests = false;
}

/**
 * Helper to wrap a promise with a timeout which rejects after the timeoutMs has elapsed, but notably
 * does not reject directly
 * @param timeoutMs - The timeout in milliseconds.
 * @param onTimeout - The function to call when the timeout is reached.
 * @param operation - The operation to wrap with the timeout. In practice, this is aimed to be used to
 * report indexedDB timeouts, so it should have its behaivour configured through dependency injection
 * @returns A promise that will resolve with the result of the operation when it completes, even if the timeout fires first.
 * The promise will only hang forever if the operation never completes.
 */
export function withTimeout<T>(
  timeoutMs: number,
  onTimeout: (error: Error) => void,
): (operation: () => Promise<T>) => Promise<T> {
  return (operation) => {
    // In test environments, disable timeout by default unless explicitly enabled
    if (isTest && !timeoutEnabledInTests) {
      return operation();
    }

    return new Promise<T>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const clearTimeoutIfNeeded = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      timeoutId = setTimeout(() => {
        const error = new IndexedDBTimeoutError('IndexedDB operation timed out');
        onTimeout(error);
      }, timeoutMs);

      operation().then(
        (result) => {
          clearTimeoutIfNeeded();
          resolve(result);
        },
        (error) => {
          clearTimeoutIfNeeded();
          reject(error);
        },
      );
    });
  };
}

const HEALTH_CHECK_DB_NAME = '__canvas_indexeddb_healthcheck__';
const HEALTH_CHECK_TIMEOUT_MS = 5000;

function performIndexeddbHealthCheck(): Promise<boolean> {
  if (!globalThis.indexedDB) {
    return Promise.resolve(false);
  }

  return new Promise<boolean>((resolve) => {
    let resolved = false;
    const testKey = 'test';
    const testValue = new Uint8Array([1, 2, 3]);

    const cleanup = () => {
      if (resolved) {
        return;
      }
      resolved = true;
      try {
        const deleteRequest = indexedDB.deleteDatabase(HEALTH_CHECK_DB_NAME);
        deleteRequest.onsuccess = () => resolve(false);
        deleteRequest.onerror = () => resolve(false);
      } catch (error) {
        console.warn('IndexedDB health check cleanup failed:', error);
        resolve(false);
      }
    };

    const timeoutId = setTimeout(() => {
      cleanup();
    }, HEALTH_CHECK_TIMEOUT_MS);

    try {
      const openRequest = indexedDB.open(HEALTH_CHECK_DB_NAME, 1);

      if (!openRequest) {
        clearTimeout(timeoutId);
        cleanup();
        return;
      }

      openRequest.onerror = () => {
        clearTimeout(timeoutId);
        cleanup();
      };

      openRequest.onblocked = () => {
        clearTimeout(timeoutId);
        cleanup();
      };

      openRequest.onsuccess = () => {
        const db = openRequest.result;

        try {
          const transaction = db.transaction(['store'], 'readwrite');
          transaction.onerror = () => {
            db.close();
            clearTimeout(timeoutId);
            cleanup();
          };
          const store = transaction.objectStore('store');

          const putRequest = store.put(testValue, testKey);
          putRequest.onsuccess = () => {
            const getRequest = store.get(testKey);
            getRequest.onsuccess = () => {
              const retrieved = getRequest.result;
              if (
                retrieved &&
                retrieved instanceof Uint8Array &&
                retrieved.length === testValue.length &&
                retrieved.every((val, idx) => val === testValue[idx])
              ) {
                transaction.oncomplete = () => {
                  db.close();
                  clearTimeout(timeoutId);
                  if (!resolved) {
                    const deleteRequest = indexedDB.deleteDatabase(HEALTH_CHECK_DB_NAME);
                    const handleDeleteResult = () => {
                      if (!resolved) {
                        resolved = true;
                        resolve(true);
                      }
                    };
                    deleteRequest.onsuccess = handleDeleteResult;
                    deleteRequest.onerror = handleDeleteResult;
                  }
                };
              } else {
                db.close();
                clearTimeout(timeoutId);
                cleanup();
              }
            };
            getRequest.onerror = () => {
              db.close();
              clearTimeout(timeoutId);
              cleanup();
            };
          };
          putRequest.onerror = () => {
            db.close();
            clearTimeout(timeoutId);
            cleanup();
          };
        } catch (error) {
          console.warn('IndexedDB health check transaction failed:', error);
          db.close();
          clearTimeout(timeoutId);
          cleanup();
        }
      };

      openRequest.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('store')) {
          db.createObjectStore('store');
        }
      };
    } catch (error) {
      console.warn('IndexedDB health check initialization failed:', error);
      clearTimeout(timeoutId);
      cleanup();
    }
  });
}

let healthCheckPromise: Promise<boolean> | null = null;

export async function checkIndexeddbHealth(): Promise<boolean> {
  if (healthCheckPromise) {
    return healthCheckPromise;
  }
  healthCheckPromise = (async () => {
    const isHealthy = await performIndexeddbHealthCheck();
    updateIndexeddbStatus(isHealthy);
    return isHealthy;
  })();
  return await healthCheckPromise;
}

export function waitForHealthCheck(): Promise<boolean> {
  if (indexeddbStatus.healthCheckComplete) {
    return Promise.resolve(indexeddbStatus.healthy);
  }
  if (healthCheckPromise) {
    return healthCheckPromise;
  }
  return checkIndexeddbHealth();
}
